/**
 * Flag a notetaker output as wrong (#14382, part of #14371) — the
 * vocabularies and the action's input contract.
 *
 * A person can mark a live panel line, a final summary claim, or an
 * extracted item **Wrong**, with a reason and an optional comment. The flags
 * are the notetaker's real-examples loop: `pnpm eval:notetaker:export-flags`
 * turns them into eval-harness cases, and the flag rate by reason is the
 * notetaker's trust metric.
 *
 * ## Two vocabularies, zero Postgres enums (ADR-0107)
 *
 * `NoteOutputFlag.reason` and `.targetKind` are `String` columns closed by
 * the `as const` tuples below. The action parses both before any write, so
 * nothing outside the tuples reaches the table.
 *
 * ## The client names a target; it never describes one
 *
 * {@link validateFlagNoteOutputInput} keeps ONLY the fields the action reads.
 * Text or span ids a client sends are not carried forward: the action
 * resolves the target from stored data and snapshots `targetText` and
 * `spanIds` itself (`note-output-flag-target.ts`), so a flag records what
 * the notetaker actually said, not what a request claims it said.
 *
 * Pure and client-safe: zod only — the flag control imports the labels.
 */

import { z } from 'zod'

export const OUTPUT_FLAG_REASONS = [
  'didnt_happen',
  'wrong_detail',
  'overstated',
  'missing_context',
  'other',
] as const

export type OutputFlagReason = (typeof OUTPUT_FLAG_REASONS)[number]

export const OUTPUT_FLAG_REASON_LABELS: Record<OutputFlagReason, string> = {
  didnt_happen: "Didn't happen",
  wrong_detail: 'Wrong detail',
  overstated: 'Overstated',
  missing_context: 'Missing context',
  other: 'Other',
}

export const outputFlagReasonSchema = z.enum(OUTPUT_FLAG_REASONS)

export function parseOutputFlagReason(value: unknown): OutputFlagReason | null {
  const parsed = outputFlagReasonSchema.safeParse(value)
  return parsed.success ? parsed.data : null
}

/**
 * What was flagged: a line of the recording bar's LIVE summary (#14379), a
 * claim of the note's final summary (#14378), or an extracted item card.
 */
export const OUTPUT_FLAG_TARGET_KINDS = [
  'live_claim',
  'summary_claim',
  'item',
] as const

export type OutputFlagTargetKind = (typeof OUTPUT_FLAG_TARGET_KINDS)[number]

export const outputFlagTargetKindSchema = z.enum(OUTPUT_FLAG_TARGET_KINDS)

export function parseOutputFlagTargetKind(
  value: unknown,
): OutputFlagTargetKind | null {
  const parsed = outputFlagTargetKindSchema.safeParse(value)
  return parsed.success ? parsed.data : null
}

/** The comment's cap, validated before any database work. */
export const NOTE_OUTPUT_FLAG_COMMENT_MAX_CHARS = 500

/**
 * The snapshot's cap. A summary section body is at most 1,200 characters
 * (`MEETING_SUMMARY_MAX_BODY_CHARS`), and no flaggable output is longer.
 */
export const NOTE_OUTPUT_FLAG_TARGET_TEXT_MAX_CHARS = 1_200

/** A cuid is ~25 chars; 200 matches every sibling action module. */
const MAX_ID_CHARS = 200

/** One message for every malformed or unreadable target (ADR-0023, #5708). */
export const NOTE_OUTPUT_FLAG_NOT_FOUND = 'Not found'

/** The snapshot as stored: trimmed, and never past the column's cap. */
export function snapshotTargetText(text: string): string {
  return text.trim().slice(0, NOTE_OUTPUT_FLAG_TARGET_TEXT_MAX_CHARS)
}

/**
 * cyrb53 — a small, stable, non-cryptographic string hash. Client-safe (no
 * `node:crypto`), and collision-resistant enough for the handful of claims
 * one summary has; a collision could only point a flag at a claim with the
 * same hash in the SAME summary.
 */
function cyrb53(input: string): string {
  let h1 = 0xdeadbeef
  let h2 = 0x41c6ce57
  for (let i = 0; i < input.length; i++) {
    const ch = input.charCodeAt(i)
    h1 = Math.imul(h1 ^ ch, 2654435761)
    h2 = Math.imul(h2 ^ ch, 1597334677)
  }
  h1 = Math.imul(h1 ^ (h1 >>> 16), 2246822507)
  h1 ^= Math.imul(h2 ^ (h2 >>> 13), 3266489909)
  h2 = Math.imul(h2 ^ (h2 >>> 16), 2246822507)
  h2 ^= Math.imul(h1 ^ (h1 >>> 13), 3266489909)
  return (4294967296 * (2097151 & h2) + (h1 >>> 0)).toString(16)
}

/**
 * A final summary claim's reference (#14378 stores a claim as its text and
 * its cited segment ids, with no id of its own).
 *
 * DERIVED FROM THE CLAIM'S CONTENT rather than its position. The note page
 * drops a claim that restates an item card (`suppressSummaryRestatements`),
 * so a rendered claim's index is not its stored index, and a regenerated
 * summary would silently re-point a positional reference at a different
 * line. A content reference either finds the same claim or finds nothing.
 */
export function summaryClaimRef(claim: {
  text: string
  spanIds: readonly string[]
}): string {
  return `sc-${cyrb53(`${claim.text}\u0000${claim.spanIds.join(',')}`)}`
}

export type FlagNoteOutputInput = {
  noteId: string
  targetKind: OutputFlagTargetKind
  targetRef: string
  reason: OutputFlagReason
  comment?: string | null
  /** The live capture — required for, and only read on, a `live_claim`. */
  recordingId?: string | null
}

export type ValidatedFlagNoteOutputInput = {
  noteId: string
  targetKind: OutputFlagTargetKind
  targetRef: string
  reason: OutputFlagReason
  comment: string | null
  recordingId: string | null
}

function isUsableId(value: unknown): value is string {
  return (
    typeof value === 'string' &&
    value.length > 0 &&
    value.length <= MAX_ID_CHARS
  )
}

/**
 * The action's input, bounded and parsed — with no database work, so an
 * over-long comment is refused before anything is read or written.
 */
export function validateFlagNoteOutputInput(
  input: unknown,
):
  | { ok: true; value: ValidatedFlagNoteOutputInput }
  | { ok: false; error: string } {
  if (typeof input !== 'object' || input === null) {
    return { ok: false, error: NOTE_OUTPUT_FLAG_NOT_FOUND }
  }
  const raw = input as Record<string, unknown>

  if (!isUsableId(raw.noteId) || !isUsableId(raw.targetRef)) {
    return { ok: false, error: NOTE_OUTPUT_FLAG_NOT_FOUND }
  }
  const targetKind = parseOutputFlagTargetKind(raw.targetKind)
  if (targetKind === null) {
    return { ok: false, error: NOTE_OUTPUT_FLAG_NOT_FOUND }
  }
  const reason = parseOutputFlagReason(raw.reason)
  if (reason === null) return { ok: false, error: 'Choose a reason' }

  let comment: string | null = null
  if (raw.comment !== undefined && raw.comment !== null) {
    if (typeof raw.comment !== 'string') {
      return { ok: false, error: 'Comment must be text' }
    }
    if (raw.comment.length > NOTE_OUTPUT_FLAG_COMMENT_MAX_CHARS) {
      return {
        ok: false,
        error: `Comment must be ${NOTE_OUTPUT_FLAG_COMMENT_MAX_CHARS} characters or less`,
      }
    }
    const trimmed = raw.comment.trim()
    comment = trimmed.length === 0 ? null : trimmed
  }

  let recordingId: string | null = null
  if (targetKind === 'live_claim') {
    if (!isUsableId(raw.recordingId)) {
      return { ok: false, error: NOTE_OUTPUT_FLAG_NOT_FOUND }
    }
    recordingId = raw.recordingId
  }

  return {
    ok: true,
    value: {
      noteId: raw.noteId,
      targetKind,
      targetRef: raw.targetRef,
      reason,
      comment,
      recordingId,
    },
  }
}

/**
 * This reader's own flags on one note, by target reference — what lets a
 * control open on "Flagged" after a reload. Item and summary-claim refs live
 * in different id spaces, so they are kept apart.
 */
export type ViewerNoteOutputFlags = {
  noteId: string
  summaryClaims: Readonly<Record<string, ViewerOutputFlag>>
  items: Readonly<Record<string, ViewerOutputFlag>>
}

/**
 * One stored flag as its author sees it again. The comment travels with the
 * reason: a re-flag saves both, so a picker that opened without the stored
 * comment would overwrite it with nothing.
 */
export type ViewerOutputFlag = {
  reason: OutputFlagReason
  comment: string | null
}

/**
 * Fold stored flag rows into {@link ViewerNoteOutputFlags}. A row whose kind
 * or reason is outside the vocabularies is dropped rather than trusted; a
 * `live_claim` row is dropped too — the live panel is gone once the note
 * page is open.
 */
export function foldViewerNoteOutputFlags(
  noteId: string,
  rows: readonly {
    targetKind: string
    targetRef: string
    reason: string
    comment: string | null
  }[],
): ViewerNoteOutputFlags {
  const summaryClaims: Record<string, ViewerOutputFlag> = {}
  const items: Record<string, ViewerOutputFlag> = {}
  for (const row of rows) {
    const reason = parseOutputFlagReason(row.reason)
    if (reason === null) continue
    const flag = { reason, comment: row.comment }
    if (row.targetKind === 'summary_claim') summaryClaims[row.targetRef] = flag
    if (row.targetKind === 'item') items[row.targetRef] = flag
  }
  return { noteId, summaryClaims, items }
}
